import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Calendar, Euro, Users, Home, Clock, Loader2, CalendarX } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useManageDashboard, type Booking, type BookingStatus } from '../../../hooks/useManagement';

const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'] as const;

const statusStyles: Record<BookingStatus, string> = {
  pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-rose-100 text-rose-800',
  completed: 'bg-slate-100 text-slate-700',
};

const eur = (n: number) => `€${n.toLocaleString('es-ES')}`;

export default function ManageDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openBooking = (id: number) => navigate(`/portal/manage/bookings?open=${id}`);
  const { data, isLoading } = useManageDashboard();
  const statusLabels: Record<BookingStatus, string> = {
    pending: t('portal.manage.status.pending'),
    confirmed: t('portal.manage.status.confirmed'),
    cancelled: t('portal.manage.status.cancelled'),
    completed: t('portal.manage.status.completed'),
  };

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-teal-500" size={32} />
      </div>
    );
  }

  const { kpis, revenueByMonth, upcoming, recent } = data;
  const hasBookings = upcoming.length > 0 || recent.length > 0;

  // Map sparse '01'..'12' revenue into a full Jan–Dic series.
  const revenueMap = new Map<string, number>();
  for (const r of revenueByMonth) {
    revenueMap.set(r.month, (revenueMap.get(r.month) ?? 0) + r.revenue);
  }
  const chartData = MONTHS_ES.map((label, i) => {
    const key = String(i + 1).padStart(2, '0');
    return { month: label, revenue: revenueMap.get(key) ?? 0 };
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('portal.manage.dashboard.title')}</h1>
        <p className="text-sm text-slate-500 mt-1">
          {format(new Date(), "EEEE, d 'de' MMMM yyyy", { locale: es })}
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <KpiCard label={t('portal.manage.dashboard.kpiRevenue')} value={eur(kpis.revenue)} sub={t('portal.manage.dashboard.kpiRevenueSub')} icon={Euro} tone="teal" />
        <KpiCard label={t('portal.manage.dashboard.kpiActive')} value={String(kpis.activeBookings)} sub={t('portal.manage.dashboard.kpiActiveSub')} icon={Calendar} tone="emerald" />
        <KpiCard
          label={t('portal.manage.dashboard.kpiPending')}
          value={String(kpis.pendingBookings)}
          sub={kpis.pendingBookings > 0 ? t('portal.manage.dashboard.kpiPendingWaiting') : t('portal.manage.dashboard.kpiPendingDone')}
          icon={Clock}
          tone="amber"
          urgent={kpis.pendingBookings > 0}
        />
        <KpiCard label={t('portal.manage.dashboard.kpiGuests')} value={String(kpis.totalGuests)} sub={t('portal.manage.dashboard.kpiGuestsSub')} icon={Users} tone="teal" />
        <KpiCard label={t('portal.manage.dashboard.kpiProperties')} value={String(kpis.properties)} sub={t('portal.manage.dashboard.kpiPropertiesSub')} icon={Home} tone="slate" />
      </div>

      {/* Pending alert */}
      {kpis.pendingBookings > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              {t('portal.manage.dashboard.pendingAlert', { count: kpis.pendingBookings })}
            </p>
            <p className="text-xs text-amber-600">{t('portal.manage.dashboard.pendingAlertNote')}</p>
          </div>
        </div>
      )}

      {!hasBookings ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4">
            <CalendarX className="w-6 h-6" />
          </div>
          <p className="text-slate-900 font-medium">{t('portal.manage.dashboard.noBookings')}</p>
          <p className="text-sm text-slate-500 mt-1">{t('portal.manage.dashboard.noBookingsSub')}</p>
        </div>
      ) : (
        <>
          {/* Revenue chart + upcoming */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">{t('portal.manage.dashboard.revenueByMonth')}</h3>
                <span className="text-sm text-slate-400">{t('portal.manage.dashboard.revenueTotal', { amount: eur(kpis.revenue) })}</span>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="kanarixRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.18} />
                      <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(v: number) => [eur(v), t('portal.manage.dashboard.revenueTooltip')]}
                    contentStyle={{ border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#14b8a6" strokeWidth={2} fill="url(#kanarixRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Upcoming check-ins */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">{t('portal.manage.dashboard.upcoming')}</h3>
              <div className="space-y-3">
                {upcoming.slice(0, 5).map((b) => (
                  <div key={b.id} onClick={() => openBooking(b.id)} role="button" tabIndex={0} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center shrink-0">
                      <div className="text-teal-700 text-xs font-bold leading-tight text-center">
                        <div>{format(parseISO(b.check_in), 'd')}</div>
                        <div className="uppercase">{format(parseISO(b.check_in), 'MMM', { locale: es })}</div>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{b.guest_name}</div>
                      <div className="text-xs text-slate-500">
                        {t('portal.manage.dashboard.nightsGuests', { nights: b.nights, guests: b.guests, total: eur(b.total) })}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${statusStyles[b.status]}`}>
                      {statusLabels[b.status]}
                    </span>
                  </div>
                ))}
                {upcoming.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">{t('portal.manage.dashboard.noUpcoming')}</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent bookings table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">{t('portal.manage.dashboard.recent')}</h3>
            </div>
            {recent.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">{t('portal.manage.dashboard.noRecent')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <Th>{t('portal.manage.dashboard.colGuest')}</Th>
                      <Th>{t('portal.manage.dashboard.colCheckIn')}</Th>
                      <Th>{t('portal.manage.dashboard.colCheckOut')}</Th>
                      <Th>{t('portal.manage.dashboard.colNights')}</Th>
                      <Th>{t('portal.manage.dashboard.colTotal')}</Th>
                      <Th>{t('portal.manage.dashboard.colStatus')}</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {recent.map((b: Booking) => (
                      <tr key={b.id} onClick={() => openBooking(b.id)} className="hover:bg-slate-50 transition-colors cursor-pointer">
                        <td className="px-6 py-3">
                          <div className="font-medium text-slate-900">{b.guest_name}</div>
                          <div className="text-xs text-slate-400">
                            {[b.guest_country, b.channel].filter(Boolean).join(' · ')}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-slate-600">{format(parseISO(b.check_in), 'd MMM yyyy', { locale: es })}</td>
                        <td className="px-6 py-3 text-slate-600">{format(parseISO(b.check_out), 'd MMM yyyy', { locale: es })}</td>
                        <td className="px-6 py-3 text-slate-600">{b.nights}</td>
                        <td className="px-6 py-3 font-medium text-slate-900">{eur(b.total)}</td>
                        <td className="px-6 py-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusStyles[b.status]}`}>
                            {statusLabels[b.status]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{children}</th>
  );
}

function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  tone,
  urgent,
}: {
  label: string;
  value: string;
  sub: string;
  icon: React.ElementType;
  tone: 'teal' | 'emerald' | 'amber' | 'slate';
  urgent?: boolean;
}) {
  const tones: Record<typeof tone, string> = {
    teal: 'bg-teal-50 text-teal-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    slate: 'bg-slate-100 text-slate-600',
  };
  return (
    <div className={`bg-white rounded-xl border p-5 ${urgent ? 'border-amber-300 shadow-sm shadow-amber-100' : 'border-slate-200'}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-500">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{sub}</div>
    </div>
  );
}
